import React from 'react';
import { cn } from '@/utils/cn';
import OptimizedImage from './OptimizedImage';
import { formatPrice } from '@/Services/format';

interface FoodOption {
    id: number;
    name: string;
    image_path?: string;
    price: number;
    portion_quantity?: number;
    food_unit?: string;
    portion_label?: string;
    weight_percentage?: number;
    daily_capacity?: number;
}

interface MenuGroupSelectorLabels {
    outOfStock?: string;
    pickLabel?: (pick: number, portionLabel: string) => string;
}

interface MenuGroupSelectorProps {
    groupName: string;
    pick: number;
    portionLabel: string;
    options: FoodOption[];
    selectedIds: number[];
    onSelectionChange: (ids: number[]) => void;
    labels?: MenuGroupSelectorLabels;
}

const defaultPickLabel = (pick: number, portionLabel: string): string => {
    if (pick === 1) return `Choisissez 1 ${portionLabel || 'option'}`;
    return `Choisissez jusqu'à ${pick} ${portionLabel || 'options'}`;
};

const MenuGroupSelector: React.FC<MenuGroupSelectorProps> = ({
    groupName,
    pick,
    portionLabel,
    options,
    selectedIds,
    onSelectionChange,
    labels,
}) => {
    const handleToggle = (foodId: number) => {
        if (selectedIds.includes(foodId)) {
            onSelectionChange(selectedIds.filter((id) => id !== foodId));
            return;
        }

        const nextIds = [...selectedIds, foodId];
        onSelectionChange(nextIds.slice(-pick));
    };

    const isComplete = selectedIds.length >= pick;

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-app-text">{groupName}</h3>
                    <span
                        className={cn(
                            'text-[10px] font-semibold px-2 py-0.5 rounded-full transition-all',
                            isComplete
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : selectedIds.length > 0
                                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                  : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                        )}
                    >
                        {selectedIds.length}/{pick}
                    </span>
                </div>
                <span className="text-[10px] text-app-muted italic">{(labels?.pickLabel ?? defaultPickLabel)(pick, portionLabel)}</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {options.map((food) => {
                    const isSelected = selectedIds.includes(food.id);
                    const isOutOfStock = (food.daily_capacity ?? 0) === 0;

                    return (
                        <button
                            type="button"
                            key={food.id}
                            disabled={isOutOfStock}
                            className={cn(
                                'relative flex flex-col items-center p-3 rounded-2xl border-2 transition-all duration-200 text-left',
                                isSelected
                                    ? 'border-app-primary bg-app-primary/5 shadow-md scale-[1.02]'
                                    : isOutOfStock
                                      ? 'border-app-border opacity-40 cursor-not-allowed'
                                      : 'border-app-border hover:border-app-primary/40 hover:bg-app-primary/5 hover:shadow-sm cursor-pointer active:scale-[0.98]'
                            )}
                            onClick={() => !isOutOfStock && handleToggle(food.id)}
                        >
                            {food.image_path && (
                                <div className="w-12 h-12 rounded-xl overflow-hidden mb-2">
                                    <OptimizedImage
                                        src={food.image_path}
                                        alt={food.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            )}
                            <span className="text-xs font-bold text-app-text text-center leading-tight">
                                {food.name}
                            </span>
                            <span className="text-[10px] text-app-muted mt-0.5">
                                {formatPrice(food.price)} · {food.portion_label || portionLabel}
                            </span>
                            {food.weight_percentage != null && (
                                <span className="text-[9px] text-app-muted mt-0.5">🍳 {food.weight_percentage}%</span>
                            )}
                            {isOutOfStock && (
                                <span className="absolute top-1 right-1 text-[8px] font-bold text-red-500 uppercase">
                                    {labels?.outOfStock ?? 'Rupture'}
                                </span>
                            )}
                            {isSelected && !isOutOfStock && (
                                <span className="absolute top-1 right-1 w-5 h-5 bg-app-primary text-white rounded-full flex items-center justify-center text-xs font-bold shadow-sm">
                                    ✓
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default MenuGroupSelector;
